/**
 * Starter templates for the SetChargingProfile UI. Match the four canonical
 * shapes from evse2's "Add profile" dropdown so QA can reach the same flow
 * without reading the OCPP spec for the 100th time.
 *
 * Each factory returns a fully-formed ChargingProfileSnapshot — pass it
 * straight to ChargingProfileRepository.upsert. The factories use `nowMs`
 * for any time anchors so the templates are reproducible (tests pin a clock;
 * UI passes Date.now()).
 */
import {
  ChargingProfileId,
  ChargingRateUnit,
  ProfileKind,
  ProfilePurpose,
  RecurrencyKind,
  TransactionId,
} from '@/domain/types';
import type { ChargingProfileSnapshot } from '@/domain';

export type TemplateKind =
  | 'absoluteW'
  | 'relativeTxProfile'
  | 'recurringDaily'
  | 'multiPeriodTaper';

export interface TemplateOptions {
  readonly id: number;
  readonly stackLevel?: number;
  readonly connectorId?: number;
  readonly nowMs: number;
  readonly transactionId?: number;
}

const DEFAULT_OPTIONS = {
  stackLevel: 1,
  connectorId: 0,
} as const;

/** Absolute schedule, single 11 kW period — the simplest "cap the station" case. */
export function absoluteWTemplate(opts: TemplateOptions): ChargingProfileSnapshot {
  const { stackLevel = DEFAULT_OPTIONS.stackLevel, connectorId = DEFAULT_OPTIONS.connectorId } =
    opts;
  return {
    id: ChargingProfileId.of(opts.id),
    stackLevel,
    purpose: ProfilePurpose.ChargePointMaxProfile,
    kind: ProfileKind.Absolute,
    connectorId,
    schedule: {
      chargingRateUnit: ChargingRateUnit.W,
      chargingSchedulePeriod: [{ startPeriod: 0, limit: 11_000 }],
      startSchedule: opts.nowMs,
    },
  };
}

/** Relative TxProfile, single 7.4 kW period — applies for the current tx only. */
export function relativeTxProfileTemplate(opts: TemplateOptions): ChargingProfileSnapshot {
  const { stackLevel = DEFAULT_OPTIONS.stackLevel, connectorId = DEFAULT_OPTIONS.connectorId } =
    opts;
  if (opts.transactionId === undefined) {
    throw new Error('relativeTxProfileTemplate requires transactionId');
  }
  return {
    id: ChargingProfileId.of(opts.id),
    stackLevel,
    purpose: ProfilePurpose.TxProfile,
    kind: ProfileKind.Relative,
    transactionId: TransactionId.of(opts.transactionId),
    connectorId,
    schedule: {
      chargingRateUnit: ChargingRateUnit.W,
      chargingSchedulePeriod: [{ startPeriod: 0, limit: 7_400 }],
    },
  };
}

/** Recurring Daily, two periods (peak/off-peak) — mirrors evse2's day-tariff example. */
export function recurringDailyTemplate(opts: TemplateOptions): ChargingProfileSnapshot {
  const { stackLevel = DEFAULT_OPTIONS.stackLevel, connectorId = DEFAULT_OPTIONS.connectorId } =
    opts;
  // Anchor at midnight UTC of the template's "today" so peak/off-peak align
  // with real time-of-day, not the install moment.
  const start = new Date(opts.nowMs);
  start.setUTCHours(0, 0, 0, 0);
  return {
    id: ChargingProfileId.of(opts.id),
    stackLevel,
    purpose: ProfilePurpose.TxDefaultProfile,
    kind: ProfileKind.Recurring,
    recurrencyKind: RecurrencyKind.Daily,
    connectorId,
    schedule: {
      chargingRateUnit: ChargingRateUnit.W,
      chargingSchedulePeriod: [
        { startPeriod: 0, limit: 22_000 }, // 00:00 → 22 kW (off-peak)
        { startPeriod: 6 * 3600, limit: 7_400 }, // 06:00 → 7.4 kW (peak)
        { startPeriod: 22 * 3600, limit: 22_000 }, // 22:00 → 22 kW
      ],
      startSchedule: start.getTime(),
    },
  };
}

/** Multi-period taper — 22 kW for 30 min, then 11 kW, then 3.7 kW. */
export function multiPeriodTaperTemplate(opts: TemplateOptions): ChargingProfileSnapshot {
  const { stackLevel = DEFAULT_OPTIONS.stackLevel, connectorId = DEFAULT_OPTIONS.connectorId } =
    opts;
  return {
    id: ChargingProfileId.of(opts.id),
    stackLevel,
    purpose: ProfilePurpose.TxDefaultProfile,
    kind: ProfileKind.Absolute,
    connectorId,
    schedule: {
      chargingRateUnit: ChargingRateUnit.W,
      chargingSchedulePeriod: [
        { startPeriod: 0, limit: 22_000 },
        { startPeriod: 30 * 60, limit: 11_000 },
        { startPeriod: 60 * 60, limit: 3_700 },
      ],
      startSchedule: opts.nowMs,
      duration: 2 * 3600,
    },
  };
}

export const TEMPLATES: Record<
  TemplateKind,
  { readonly label: string; readonly build: (opts: TemplateOptions) => ChargingProfileSnapshot }
> = {
  absoluteW: { label: 'Absolute (CP-wide, 11 kW)', build: absoluteWTemplate },
  relativeTxProfile: {
    label: 'Relative TxProfile (7.4 kW)',
    build: relativeTxProfileTemplate,
  },
  recurringDaily: {
    label: 'Recurring Daily (off-peak / peak)',
    build: recurringDailyTemplate,
  },
  multiPeriodTaper: {
    label: 'Multi-period taper (22 → 11 → 3.7 kW)',
    build: multiPeriodTaperTemplate,
  },
};
